/**
 * use-anchored-menu — open/close + viewport placement for a small popover
 * anchored under a trigger button.
 *
 * The preview panel lives inside a scrolling column inside the optimizer
 * drawer, so the menu is portaled (a plain absolute child would be clipped by
 * the column's `overflow-y-auto`). Mirrors the CsvTemplateMenu pattern: close
 * on outside mousedown, on Escape, and on any scroll that did not originate
 * inside the menu itself.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface AnchoredMenuBox {
  top: number;
  left: number;
}

export interface AnchoredMenu {
  open: boolean;
  setOpen: (next: boolean) => void;
  toggle: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  menuRef: React.RefObject<HTMLDivElement>;
  box: AnchoredMenuBox;
}

/** Keep the panel inside the viewport when the trigger sits near an edge. */
function placeUnder(rect: DOMRect, width: number): AnchoredMenuBox {
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
  return { top: rect.bottom + 4, left };
}

export function useAnchoredMenu(width: number): AnchoredMenu {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<AnchoredMenuBox>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (el) setBox(placeUnder(el.getBoundingClientRect(), width));
  }, [width]);

  useEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (t && menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, reposition]);

  const toggle = useCallback(() => setOpen((p) => !p), []);
  return { open, setOpen, toggle, triggerRef, menuRef, box };
}
